import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Sparkles, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Activity, ArrowLeft, Clock
} from "lucide-react";

const STATUS_CONFIG = {
  operational: { label: "Operational", color: "text-emerald-400", bg: "bg-emerald-500", dot: "bg-emerald-400", banner: "All Systems Operational", bannerBg: "rgba(16,185,129,0.08)", bannerBorder: "rgba(16,185,129,0.2)", Icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "text-amber-400", bg: "bg-amber-500", dot: "bg-amber-400", banner: "Some Systems Experiencing Issues", bannerBg: "rgba(245,158,11,0.08)", bannerBorder: "rgba(245,158,11,0.2)", Icon: AlertTriangle },
  major_outage: { label: "Major Outage", color: "text-red-400", bg: "bg-red-500", dot: "bg-red-400", banner: "Major System Outage", bannerBg: "rgba(239,68,68,0.08)", bannerBorder: "rgba(239,68,68,0.2)", Icon: XCircle },
  down: { label: "Down", color: "text-red-400", bg: "bg-red-500", dot: "bg-red-400" },
  auth_error: { label: "Auth Error", color: "text-red-400", bg: "bg-red-500", dot: "bg-red-400" },
  not_configured: { label: "Not Configured", color: "text-zinc-500", bg: "bg-zinc-600", dot: "bg-zinc-500" },
  unknown: { label: "Unknown", color: "text-zinc-500", bg: "bg-zinc-600", dot: "bg-zinc-500" },
};

function UptimeBars({ bars }) {
  return (
    <div className="flex items-end gap-[2px] h-8">
      {bars.map((pct, i) => {
        let color = "bg-zinc-800";
        if (pct >= 0) {
          color = pct === 100 ? "bg-emerald-500" : pct >= 80 ? "bg-amber-500" : "bg-red-500";
        }
        return (
          <div
            key={i}
            className={cn("w-[6px] rounded-sm transition-all", color)}
            style={{ height: pct >= 0 ? `${Math.max(pct * 0.28 + 4, 4)}px` : "4px", opacity: pct < 0 ? 0.2 : 1 }}
            title={pct >= 0 ? `Hour ${i}: ${pct}% uptime` : `Hour ${i}: No data`}
          />
        );
      })}
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const [statusRes, incidentsRes] = await Promise.all([
        axios.get(`${API}/status/public`),
        axios.get(`${API}/status/incidents`),
      ]);
      setData(statusRes.data);
      setIncidents(incidentsRes.data.incidents || []);
      setLastRefresh(new Date());
    } catch {
      // Keep existing data if available
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const overall = data ? STATUS_CONFIG[data.overall] || STATUS_CONFIG.unknown : STATUS_CONFIG.operational;

  return (
    <div className="min-h-screen" style={{ background: "#060912", color: "#fff" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(6,9,18,0.85)", borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
              <Sparkles className="w-4 h-4 text-red-500" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-base font-medium tracking-tight">Omni</span>
            <span className="text-zinc-600 mx-1">/</span>
            <span className="text-sm text-zinc-400">System Status</span>
          </Link>
          <button
            data-testid="status-refresh-btn"
            onClick={fetchStatus}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs text-zinc-400 hover:text-white border transition-all"
            style={{ borderColor: "rgba(255,255,255,0.08)" }}
          >
            <RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} />
            Refresh
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Overall Banner */}
            <div
              data-testid="status-overall-banner"
              className="rounded-2xl p-6 border mb-10 flex items-center gap-4"
              style={{ background: overall.bannerBg, borderColor: overall.bannerBorder }}
            >
              <overall.Icon className={cn("w-7 h-7", overall.color)} strokeWidth={1.5} />
              <div>
                <h1 className="font-heading text-xl font-medium tracking-tight">{overall.banner}</h1>
                <p className="text-xs text-zinc-500 mt-1">
                  Last checked {lastRefresh ? lastRefresh.toLocaleTimeString() : "—"} · Auto-refreshes every 60s
                </p>
              </div>
            </div>

            {/* Service List */}
            <div className="space-y-3">
              {data.services.map(svc => {
                const cfg = STATUS_CONFIG[svc.status] || STATUS_CONFIG.unknown;
                return (
                  <div
                    key={svc.id}
                    data-testid={`status-service-${svc.id}`}
                    className="rounded-xl p-5 border transition-all"
                    style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-2.5 h-2.5 rounded-full", cfg.dot)} />
                        <div>
                          <p className="text-sm font-medium">{svc.name}</p>
                          <p className="text-[11px] text-zinc-600">{svc.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={cn("text-xs font-medium", cfg.color)}>{cfg.label}</span>
                        <span className="text-[11px] text-zinc-600">{svc.uptime_24h}% uptime</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <UptimeBars bars={svc.bars} />
                      <div className="flex items-center gap-1 text-[10px] text-zinc-700 ml-4 flex-shrink-0">
                        <span>24h ago</span>
                        <div className="w-8 h-px bg-zinc-800" />
                        <span>Now</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-8 flex flex-wrap items-center gap-5 text-[11px] text-zinc-600">
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Operational</div>
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" /> Degraded</div>
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" /> Outage</div>
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-zinc-800" /> No Data</div>
            </div>

            {/* Incident History */}
            {incidents.length > 0 && (
              <div className="mt-14" data-testid="incident-history-section">
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-4 h-4 text-zinc-500" />
                  <h2 className="font-heading text-lg font-medium tracking-tight">Incident History</h2>
                </div>
                <div className="space-y-3">
                  {incidents.slice(0, 10).map((inc, i) => {
                    const isOp = inc.status === "operational";
                    return (
                      <div
                        key={i}
                        data-testid={`incident-${i}`}
                        className="rounded-xl p-4 border"
                        style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", isOp ? "bg-emerald-500" : "bg-amber-500")} />
                            <p className="text-sm font-medium">{inc.title}</p>
                          </div>
                          <p className="text-[11px] text-zinc-600">{inc.date}</p>
                        </div>
                        <p className="text-xs text-zinc-500 ml-4">{inc.detail}</p>
                        {inc.services && inc.services.length > 0 && (
                          <div className="flex gap-2 mt-2 ml-4">
                            {inc.services.map(s => (
                              <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border text-zinc-500" style={{ borderColor: "rgba(255,255,255,0.08)" }}>{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer info */}
            <div className="mt-12 pt-8 border-t text-center" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
              <p className="text-xs text-zinc-600">
                Monitoring {data.checked_count} core services · Updated continuously
              </p>
              <div className="flex items-center justify-center gap-4 mt-4">
                <Link to="/" className="text-xs text-zinc-500 hover:text-white transition-colors">
                  <ArrowLeft className="w-3 h-3 inline mr-1" />Back to Omni
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-32">
            <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Unable to load status. Please try again.</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&display=swap');
        .font-heading { font-family: 'Outfit', sans-serif; }
      `}</style>
    </div>
  );
}
